// App.js with 404 handling
import React, { useEffect, Suspense, useRef } from 'react';
import { BrowserRouter as Router, Route, Routes, useLocation, useNavigate } from 'react-router-dom';
import { CSSTransition, TransitionGroup } from 'react-transition-group';
import formbricks from "@formbricks/js";
import Header from './components/Header';
import Footer from './components/Footer';
import Loading from './components/Loading';
import CookieConsentBanner from './components/CookieConsentBanner'; // Import the cookie banner
import Home from './pages/Home';
import About from './pages/About';
import Newsletter from './pages/Newsletter';
import Contact from './pages/Contact';
import CarouselPage from './pages/Carousel';
import BookingPage from './pages/Prenota';
import Privacy from './pages/Privacy';
import TermsAndCondition from './pages/Terms';
import EbookThankYouPage from './pages/Ebook';
import WebinarLandingPage from './pages/Webinar';
import VideoPlayerTest from './pages/VideoPlayerTest';
import AudioPlayer from './pages/AudioPlayerTest';
import LoginRegistrationForm from './pages/Login';
import Profile from './pages/Profile';
import ProfileEdit from './pages/ProfileEdit';
import ProtectedRoute from './components/ProtectedRoute';
import { AuthProvider } from './context/AuthContext';
import { useAuth } from './context/AuthContext';
import SimplePurchasePage from './pages/purchase_test';
import IMieiContenutiPage from './pages/IMieiContenutiPage'
import ContentItemPage from './pages/ContentItemPage';
import Page from './pages/products/Il-cibo-che-purifica';
import NotFound from './pages/NotFound'; // Import the NotFound component
import './App.css';
import { authService } from './authService';
import IlNyasaPage from './pages/products/il-nyasa';

// ScrollToTop component to reset scroll position on navigation
const ScrollToTop = () => {
  const { pathname } = useLocation();
  
  useEffect(() => {
    window.scrollTo(0, 0);
  }, [pathname]);
  
  return null;
};

const App = () => {
  const formbricksInitialized = useRef(false);

  useEffect(() => {
    // Initialize development token
    authService.initDevToken();
  }, []);

  useEffect(() => {
    // Initialize Formbricks only once
    if (typeof window !== "undefined" && !formbricksInitialized.current) {
      formbricksInitialized.current = true;
      try {
        formbricks.init({
          environmentId: "cm874pr1y0020mu018mpdw43r", 
          apiHost: "https://formbrick.horoboro.com",
        });
        console.log("Formbricks initialized successfully");
      } catch (error) {
        console.error("Error initializing Formbricks:", error);
      }
    }

    const updateContentHeight = () => {
      const headerElement = document.querySelector('header');
      if (headerElement) {
        const headerHeight = headerElement.offsetHeight;
        document.documentElement.style.setProperty('--header-height', `${headerHeight}px`);
        document.documentElement.style.setProperty('--content-height', `calc(100vh - ${headerHeight}px)`);
      }
    };
    
    setTimeout(updateContentHeight, 0);
    window.addEventListener('resize', updateContentHeight);
    
    return () => window.removeEventListener('resize', updateContentHeight);
  }, []);
  
  return (
    <AuthProvider>
      <Router>
        <AppContent />
      </Router>
    </AuthProvider>
  );
};

// Separate component to access auth context within Router
const AppContent = () => {
  const { loading, authInitialized } = useAuth();
  
  // Import AuthStatus component
  const AuthStatus = React.lazy(() => import('./components/AuthStatus'));
  
  // Show minimal loading state when auth is initializing
  if (loading && !authInitialized) {
    return (
      <div className="flex flex-col min-h-screen">
        <div className="flex-grow flex items-center justify-center">
          <div className="animate-spin rounded-full h-12 w-12 border-t-2 border-b-2 border-green-500"></div>
        </div>
      </div>
    );
  }
  
  return (
    <div className="flex flex-col min-h-screen">
      <ScrollToTop /> {/* Added ScrollToTop component here */}
      <Header iconPath="/icons/favicon.svg" />
      
      <PageTransitions />
      
      <Footer />
      
      {/* Add Cookie Consent Banner */}
      <CookieConsentBanner />
      
      {/* Auth Status Debugger - only visible in development 
      {process.env.NODE_ENV === 'development' && (
        <React.Suspense fallback={null}>
          <AuthStatus showDetails={true} />
        </React.Suspense>
      )}*/}
    </div>
  );
};


const PageTransitions = () => {
  const location = useLocation();
  
  return (
    <TransitionGroup>
      <CSSTransition key={location.key} classNames="fade" timeout={300}>
        <Routes location={location}>
          <Route path="/" element={<Home />} />
          <Route path="/about" element={<About />} />
          <Route path="/newsletter" element={<Newsletter/>} />
          <Route path="/contattami" element={<Contact />} />
          <Route path="/prenota" element={<BookingPage />} />
          <Route path="/privacy" element={<Privacy />} />
          <Route path="/terms" element={<TermsAndCondition />} />
          <Route path="/videoplayertest" element={<VideoPlayerTest />} />
          <Route path="/audioplayertest" element={<AudioPlayer />} />
          <Route path="/ebook-cucina-consapevole-facile" element={<EbookThankYouPage />} />
          <Route path="/webinar-leggeri-e-sfiammati" element={<WebinarLandingPage />} />
          <Route path="/il-cibo-che-purifica" element={<Page />} />
          <Route path="/il-nyasa" element={<IlNyasaPage />} />
          <Route path="/test-purchase" element={<SimplePurchasePage />} />
          <Route path="/login" element={<LoginRegistrationForm />} />
          
          {/* Content related routes */}
          <Route path="/contenuti" element={
            <ProtectedRoute>
              <IMieiContenutiPage />
            </ProtectedRoute>
          } />
          
          {/* Fallback route using IDs (for backward compatibility) */}
          <Route path="/contenuti/view/:productId/:itemId" element={
            <ProtectedRoute>
              <ContentItemPage />
            </ProtectedRoute>
          } />
          
          {/* New slug-based route */}
          <Route path="/contenuti/:productSlug/:itemSlug" element={
            <ProtectedRoute>
              <ContentItemPage />
            </ProtectedRoute>
          } />
          
          <Route path="/profilo" element={
            <ProtectedRoute>
              <Profile />
            </ProtectedRoute>
          } />

          {/* 404 Not Found route - must be last */}
          <Route path="*" element={<NotFound />} />
          
        </Routes> 
      </CSSTransition>
    </TransitionGroup>
  );
};

export default App;